import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { classNames } from "@/react/helpers/twMergeWithCN";

import type { ReportFormData } from "./ReportConnectionModal";
import { useReportConnectionModal } from "./ReportConnectionModal";
import type { MemberInfo } from "./types";
import { useFlagConnectionForUser } from "./useFlagConnectionForUser";

interface ConnectionNoteProps {
  className?: string;
  collapse?: boolean;
  connectionId?: string;
  initiatorIsAdmin?: boolean;
  lineClamp?: number;
  member?: MemberInfo;
  note?: string;
}

export const ConnectionNote = ({
  note,
  collapse = false,
  lineClamp = 3,
  className,
  connectionId,
  initiatorIsAdmin,
  member,
}: ConnectionNoteProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowSeeMore, setShouldShowSeeMore] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const { currentCommunitySettings } = usePunditUserContext();
  const reportModal = useReportConnectionModal();

  const shouldShowActionsDropdown =
    connectionId &&
    member?.public_uid &&
    !initiatorIsAdmin &&
    Boolean(currentCommunitySettings?.connect_flagging_enabled);

  const { flagConnectionForUser, isFlagging } = useFlagConnectionForUser({
    member,
  });

  const handleReportSubmit = (data: ReportFormData) => {
    if (!connectionId) return;
    flagConnectionForUser({
      connectionId,
      reported_reason_body: data.reported_reason_body,
      reported_reason_type: data.reported_reason_type,
    });
  };

  const handleOpenReportModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void reportModal.show({
      onSubmit: handleReportSubmit,
      isLoading: isFlagging,
    });
  };

  useEffect(() => {
    if (!collapse || !textRef.current) return;

    const element = textRef.current;
    const isOverflowing = element.scrollHeight > element.clientHeight;
    setShouldShowSeeMore(isOverflowing);
  }, [note, collapse]);

  const handleSeeMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
  };

  const isConnectPopoverPanel = useIsConnectPopoverPanel();

  if (!note) {
    return null;
  }

  return (
    <div
      className={classNames(
        "bg-secondary !mt-2 w-full rounded-lg px-3 py-2.5",
        isConnectPopoverPanel ? "mb-1" : "mb-3",
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <div className="relative min-w-0 flex-1">
          <div
            ref={textRef}
            // eslint-disable-next-line tailwindcss/no-custom-classname -- I'm dynamically setting the line clamp number
            className={classNames(
              collapse && "whitespace-pre-wrap",
              collapse && !isExpanded && `line-clamp-${lineClamp}`,
            )}
          >
            <Typography.BodyXs color="text-darkest" weight="normal">
              {note}
            </Typography.BodyXs>
          </div>
          {collapse && shouldShowSeeMore && !isExpanded && (
            <div className="bg-secondary absolute bottom-0 right-0 pl-2">
              <button
                type="button"
                onClick={handleSeeMore}
                className="text-dark text-xs font-semibold"
              >
                {t("settings.connect_audience.see_more")}
              </button>
            </div>
          )}
        </div>
        {shouldShowActionsDropdown && (
          <Dropdown
            direction="bottom-end"
            buttonClassName="flex shrink-0 items-center justify-center h-6 w-6 rounded-md hover:bg-tertiary focus-visible:outline-secondary focus-visible:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            button={
              <TippyV2
                content={t("messaging.more_actions")}
                className="flex items-center"
              >
                <Icon
                  type="16-menu-dots-horizontal"
                  size={16}
                  className="text-default"
                  aria-label={t("messaging.more_actions")}
                />
              </TippyV2>
            }
            dataTestId="connection-note-actions-dropdown"
          >
            <Dropdown.ItemWithLink
              data-testid="flag-button"
              linkType="button"
              onClick={handleOpenReportModal}
            >
              {t("report")}
            </Dropdown.ItemWithLink>
          </Dropdown>
        )}
      </div>
    </div>
  );
};
